import { Search, HelpCircle } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { scoreRanges, scoreRules, segmentStatuses } from '../../utils/qualityConstants';
//import { languages } from '../../utils/languages';
import { googleSupportedLanguages } from '../../services/languageService';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { Button } from '../ui/button';
import { getSegmentErrors } from '../../services/segmentService';
import LimitBanner from '../common/LimitBanner';
import { highlightDifferences } from '../../utils/textUtils';
import alertCircle from '../../assets/icons/alert_circle.svg';  
import download from '../../assets/icons/download.svg';

const COLUMN_COUNT = 7;
const MAX_SKELETON_LOADER_TABLE_ROWS = 10;

const getSkeletonLoader = (tableRowsCount) => {
  const rowsCount = tableRowsCount > MAX_SKELETON_LOADER_TABLE_ROWS ? MAX_SKELETON_LOADER_TABLE_ROWS : tableRowsCount;
  return Array.from({ length: rowsCount }, (_, i) => {
    const renderCells = Array.from({ length: COLUMN_COUNT }, (_, j) => (
      <td key={`skeleton-cell-${i}-${j}`} className="p-4">
        <p className="bg-[#c1c1c1] w-100 h-[20px] rounded"/>
      </td>
    ));

    return <tr key={`skeleton-row-${i}`} className="px-4 py-3 text-sm text-gray-500 text-center animate-pulse">
      {renderCells}
    </tr>
  })
}

const QualityScore = ({ segment, isDisabled }) => {
    const getScoreColor = (score) => {
        if (isDisabled) return 'bg-gray-100';
        if (score >= 91) return 'bg-green-500'; // Publish-Ready
        if (score >= 70) return 'bg-orange-500'; // Flawed
        if (score >= 1) return 'bg-red-500'; // Unusable
        return 'bg-[rgb(17,24,39)]'; // Error (0) — visually black
    };

    return (
        <div className="flex justify-left">
            <div className="flex items-center space-x-2">
                <div
                    className={`w-4 h-4 rounded-full ${getScoreColor(segment.score)} ${
                        isDisabled && 'border border-[#E2E8F0]'
                    }`}
                />
            </div>
        </div>
    );
};

const StatusBadge = ({ status, isDisabled }) => {
  const statusInfo = segmentStatuses[status] || segmentStatuses[0];

  return (
    <span className={`text-sm ${isDisabled ? 'text-gray-400' : statusInfo.color}`}>
      {statusInfo.label}
    </span>
  );
};

const ResultsTable = ({ 
  results = [],
  className, 
  disableShowMore, 
  hideFilter,
  sourceLanguage,
  targetLanguage,
  segmentsCount,
  evaluationId,
  limits
}) => {
  const [visibleCount, setVisibleCount] = useState(15);
  const [searchQuery, setSearchQuery] = useState('');
  const [scoreRange, setScoreRange] = useState('All Scores');
  const [segmentErrors, setSegmentErrors] = useState({});

  const handleDownloadCSV = () => {
    if (!evaluationId) return;
    window.location.href = `https://services-375925469890.us-central1.run.app/download/${evaluationId}`;
  };

  useEffect(() => {
    const fetchErrors = async () => {
      const segmentsWithServerId = results.filter(result => result.serverId);
      if (segmentsWithServerId.length === 0) return;
      try {
        const ids = segmentsWithServerId.map(result => result.serverId);
        const response = await getSegmentErrors(ids);
        
        setSegmentErrors(response || {});
      } catch (error) {
        console.error('Failed to fetch errors:', error);
      }
    };

    fetchErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps   
  }, []);

  const handleShowMore = () => {
    setVisibleCount(prev => Math.min(prev + 10, results.length));
  };

  // Memoize filteredResults
  const filteredResults = useMemo(() => {
    const matchRule = scoreRules[scoreRange] || (() => true);

    return results.filter(result => {
      const matchesScoreRange = matchRule(result.score);

      if (!searchQuery) return matchesScoreRange;

      const query = searchQuery.toLowerCase();
      const matchesSearch =
        (result?.source ?? '').toLowerCase().includes(query) ||
        (result?.target ?? '').toLowerCase().includes(query) ||
        (result?.comments ?? '').toLowerCase().includes(query);

      return matchesScoreRange && matchesSearch;
    });
  }, [results, scoreRange, searchQuery]);

  // Memoize visibleResults
  const visibleResults = useMemo(
    () => filteredResults.slice(0, visibleCount),
    [filteredResults, visibleCount]
  );

  const segmentErrorMap = useMemo(() => {
    if (!Array.isArray(segmentErrors)) return {};
  
    return segmentErrors.reduce((map, e) => {
      if (!map[e.segmentId]) {
        map[e.segmentId] = [];
      }
      map[e.segmentId].push(e);
      return map;
    }, {});
  }, [segmentErrors]);

  const rangeCounts = useMemo(() => {
    const counters = Object.fromEntries(
      scoreRanges.map(r => [r.label, 0])
    );
  
    for (const { score } of results) {
      for (const range of scoreRanges) {
        const matchRule = scoreRules[range.label];
        if (matchRule && matchRule(score)) {
          counters[range.label]++;
        }
      }
    }
    return counters;
  }, [results]);

  const hasMoreResults = filteredResults.length > visibleCount;
  const renderSkeletonLoader = segmentsCount && getSkeletonLoader(segmentsCount);
  const renderNoResults = segmentsCount
      ? renderSkeletonLoader
      : <tr key="no-results">
        <td colSpan="8" className="px-4 py-3 text-sm text-gray-500 text-center">
          No results found
        </td>
      </tr>

  const getLanguageName = (code) => {
    if (code === 'Detect language') return '';
    return googleSupportedLanguages.find(l => l.code === code)?.name || code;
  };

  const getErrorSeverityColor = (severity) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'text-red-500';
      case 'warning':
        return 'text-yellow-500';
      case 'info':
        return 'text-blue-500';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <div>
      <div className="w-full">
        <div className="border border-t-0 w-full rounded-lg relative">
          <div 
            className={`md:${visibleResults.length > 3 ? 'sticky top-[80px] md:shadow-[0_-3px_0_rgba(255,255,255,1)]' : ''} 
            z-20 bg-white border-t border-gray-200 bg-white rounded-lg`}
          >
            <div className="border-b px-4 py-4">
              <div className="flex flex-col gap-2 md:flex-row md:justify-between md:items-center md:space-x-4">
                {!hideFilter && (
                  <div className="flex flex-col gap-3 md:flex-row w-full md:w-auto">
                    <div className="relative w-full md:w-auto">
                      <Select value={scoreRange} onValueChange={setScoreRange}>
                        <SelectTrigger className="w-full md:w-48 xl:w-56 bg-white hover:bg-gray-50 border-gray-200">
                          <SelectValue>
                            {scoreRange}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {scoreRanges.map((range) => (
                            <SelectItem key={range.label} value={range.label}>
                              {range.label} ({rangeCounts[range.label]})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="relative w-full md:w-[60%] xl:w-[460px]">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        placeholder="Search translations..."
                        className="block w-full pl-9 pr-3 py-1.5 h-[36px] border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-[rgb(17,24,39)] focus:border-[rgb(17,24,39)] text-sm"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>
                )}
                {!hideFilter && (
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={handleDownloadCSV}
                      className="inline-flex items-center px-3 py-1.5 h-[32px] w-[80px]
                      border-none rounded-md bg-[#020817] shadow-[0px_1px_2px_0px_#0000000D]
                      text-base font-medium text-white hover:bg-[#29303D] focus:outline-none"
                    >
                      <img src={download} alt='download icon' className="h-4 w-4" />
                      <span className="ml-2">CSV</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto xl:overflow-x-visible">
            <div className="min-w-[1010px] xl:min-w-[900px]">
              {/* Table Header */}
              <div className={`bg-gray-50 md:bg-gray-50 border-b ${visibleResults.length > 3 ? 'xl:sticky top-[145px] z-20' : ''}`}>
                <div className="flex">
                  <div className="px-4 py-2 text-left text-sm font-medium text-gray-500 w-[46px]">#</div>
                  <div className="px-4 py-2 text-left text-sm font-medium text-gray-500 md:w-[203px] w-[200px]">{getLanguageName(sourceLanguage)}</div>
                  <div className="px-4 py-2 text-left text-sm font-medium text-gray-500 md:w-[203px] w-[200px]">{getLanguageName(targetLanguage)}</div>
                  <div className="px-4 py-2 text-left text-sm font-medium text-gray-500 md:w-[117px] w-[116px]">Status</div>
                  <div className="px-4 py-2 text-left text-sm font-medium text-gray-500 w-[94px]">Score</div>
                  <div className="px-4 py-2 text-left text-sm font-medium text-gray-500 md:w-[182px] w-[160px]">Comments</div>
                  <div className="px-4 py-2 text-left text-sm font-medium text-gray-500 md:w-[182px] w-[160px]">Corrected</div>
                </div>
              </div>

              {/* Warning Message */}
              <div className="px-4 py-2 text-center text-xs text-gray-500 bg-gray-50 border-b">
                <div className="flex items-center justify-center space-x-2 text-gray-600">
                  <img src={alertCircle} alt="alert" className="h-4 w-4" />
                  <p className="text-sm">Even sophisticated LLMs can make mistakes. Use the results carefully.</p>
                </div>
              </div>  

              {/* Table Body */}
              <div className="w-full xl:w-auto pt-[1px]">
                <table className="sm:w-unset w-full">
                <colgroup>
                  <col className="w-[38px]" />
                  <col className="w-[200px]" />
                  <col className="w-[200px]" />
                  <col className="w-[114px]" />
                  <col className="w-[88px]" />
                  <col className="w-[180px]" />
                  <col className="w-[180px]" />
                </colgroup>
                  <tbody className={className}>
                    {visibleResults.length > 0 ? (
                      (() => {
                        let warningShown = false;
                        let disabledRows = false;
                        return visibleResults.map((result, index) => {
                          const showWarning = !warningShown && result.status === 4;
                          if (showWarning) {
                            warningShown = true;
                            disabledRows = true;
                            return [
                              <tr key={`warning-row-${index}`}>
                                <td colSpan="7" className="px-2.5 py-2.5 bg-[#F9FAFB] border-b border-[#E2E8F0]"> 
                                  <LimitBanner className="flex justify-center p-4 bg-[#F1F5F9] border border-[#2563EB] rounded-[8px] min-h-[74px]" limits={limits} />
                                </td>
                              </tr>,
                              <tr 
                                key={`result-${result.id || index}`} 
                                className={`group border-b last:border-b-0 ${disabledRows ? 'bg-[#F9FAFB]' : ''}`}
                              >
                                <td className={`w-[40px] px-4 py-2 text-sm ${disabledRows ? 'text-[#9CA3AF]' : 'text-gray-500'}`}>{index + 1}</td>
                                <td className="px-4 py-2 md:w-[200px] w-[160px]">
                                  <div>
                                    <p className={`text-sm ${disabledRows ? 'text-[#9CA3AF]' : 'text-gray-900'} whitespace-pre-wrap break-words [word-break:break-word]`}>{result.source}</p>
                                  </div>
                                </td>
                                <td className="px-4 py-2 md:w-[200px] w-[160px]">
                                  <div className="max-w-md">
                                    <p className={`text-sm ${disabledRows ? 'text-[#9CA3AF]' : 'text-gray-900'} whitespace-pre-wrap break-words [word-break:break-word]`}>{result.target}</p>
                                  </div>
                                </td>
                                <td className="px-4 py-2 w-[116px]">
                                  <StatusBadge status={result.status} isDisabled={disabledRows} />
                                </td>
                                <td className="px-4 py-2 w-[88px]">
                                  <QualityScore segment={result} isDisabled={disabledRows} />
                                </td>
                                <td className="px-4 py-2 md:w-[180px] w-[160px]">
                                  <div className="max-w-[200px]">
                                    <div className="flex items-center gap-1">
                                      <p className={`text-sm ${disabledRows ? 'text-[#9CA3AF]' : 'text-gray-600'} whitespace-pre-wrap`}>{result.comments}</p>
                                      {(() => {
                                        const error = segmentErrorMap[result.serverId];
                                        if (!error) return null;
                                        return (
                                          <TooltipProvider key={`tooltip-${result.serverId}`}>
                                            <Tooltip delayDuration={0}>
                                              <TooltipTrigger asChild>
                                                <Button variant="ghost" size="sm" className="p-0 h-auto opacity-0 group-hover:opacity-100 transition-opacity">
                                                  <HelpCircle className="w-4 h-4 text-gray-400" />
                                                </Button>
                                              </TooltipTrigger>
                                              <TooltipContent side="bottom" className="max-w-[280px]">
                                                <span className={`text-xs ${getErrorSeverityColor(error.severity)}`}>
                                                  {error.severity}: 
                                                </span>
                                                <span className="text-xs text-gray-600">
                                                  {' ' + error.explanation}
                                                </span>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-2 md:w-[180px] w-[160px]">
                                  <div className="max-w-md">
                                    <p className={`text-sm ${disabledRows ? 'text-[#9CA3AF]' : 'text-gray-900'}  whitespace-pre-wrap`} style={{ wordBreak: 'break-word' }}>{result.corrected}</p>
                                  </div>
                                </td>
                              </tr>
                            ];
                          }
                          return (
                            <tr 
                              key={`result-${result.id || index}`} 
                              className={`group border-b last:border-b-0 ${disabledRows ? 'bg-[#F9FAFB]' : ''}`}
                            >
                              <td className={`w-[40px] px-4 py-2 text-sm ${disabledRows ? 'text-[#9CA3AF]' : 'text-gray-500'}`}>
                                {index + 1}
                              </td>
                              <td className="px-4 py-2 md:w-[200px] w-[160px]">
                                <p className={`text-sm ${disabledRows ? 'text-[#9CA3AF]' : 'text-gray-900'} whitespace-pre-wrap break-words [word-break:break-word]`}>{result.source}</p>
                              </td>
                              <td className="px-4 py-2 md:w-[200px] w-[160px]">
                                <p className={`text-sm ${disabledRows ? 'text-[#9CA3AF]' : 'text-gray-900'} whitespace-pre-wrap break-words [word-break:break-word]`}>
                                  {result.corrected ? highlightDifferences(result.target, result.corrected, 'removed') : result.target}                            
                                </p>
                              </td>
                              <td className="px-4 py-2 w-[116px]">
                                <StatusBadge status={result.status} isDisabled={disabledRows} />
                              </td>
                              <td className="px-4 py-2 w-[88px]">
                                <QualityScore segment={result} isDisabled={disabledRows} />
                              </td>
                              <td className="px-4 py-2 md:w-[180px] w-[160px]">
                                <div className="max-w-[200px]">
                                  <div className="flex items-center gap-1">
                                    <p className={`text-sm ${disabledRows ? 'text-[#9CA3AF]' : 'text-gray-600'} whitespace-pre-wrap`}>{result.comments}</p>
                                    {(() => {
                                      const error = segmentErrorMap[result.serverId];
                                      if (!error) return null;
                                      return (
                                        <TooltipProvider key={`tooltip-${result.serverId}`}>
                                          <Tooltip delayDuration={0}>
                                            <TooltipTrigger asChild>
                                              <Button variant="ghost" size="sm" className="p-0 h-auto opacity-0 group-hover:opacity-100 transition-opacity">
                                                <HelpCircle className="w-4 h-4 text-gray-400" />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom" className="max-w-[400px] p-3">
                                              {error.map((err, index) => (
                                                <div key={index} className={index > 0 ? 'mt-2 pt-2 border-t border-gray-200' : ''}>
                                                  <span className={`text-xs font-medium ${getErrorSeverityColor(err.severity)}`}>
                                                    {err.severity}: 
                                                  </span>
                                                  <span className="text-xs text-gray-600">
                                                    {' ' + err.explanation}
                                                  </span>
                                                </div>
                                              ))}
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-2 md:w-[180px] w-[160px]">
                                <div className="max-w-md">
                                  <p className={`text-sm ${disabledRows ? 'text-[#9CA3AF]' : 'text-gray-900'}  whitespace-pre-wrap`} style={{ wordBreak: 'break-word' }}>
                                    {result.corrected ? highlightDifferences(result.target, result.corrected, 'added') : ''}
                                  </p>
                                </div>
                              </td>
                            </tr>
                          );
                        });
                      })()
                    ) : (
                        renderNoResults
                    )}
                  </tbody>
                </table>
              </div>

              {!disableShowMore && hasMoreResults && (
                <div className="px-4 py-3 bg-white border-t rounded-b-lg">
                  <button
                    onClick={handleShowMore}
                    className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors duration-150 ease-in-out"
                  >
                    Show More
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsTable; 